import { useDispatch } from "react-redux";
import useMerchantWebPortalAxios from "../../utils/axiosWeb";
import useResponse from "../../app/useResponse";
import { settingBillsData } from "../../features/billsPageSlice";
import {
    buildCatalogOptions,
    buildCorporatePurchasePayload,
    buildCorporatePurchaseResponse,
    buildCountryGroups,
    buildCoverageForDestination,
    buildPlansForDestination,
    getEsimCatalog,
} from "./EsimCatalogAdapter";

let catalogRequest = null;
let catalogWebKey = null;

const loadCatalog = async (client) => {
    const currentWebKey = sessionStorage.getItem("webKey") || "";

    if (!catalogRequest || catalogWebKey !== currentWebKey) {
        catalogWebKey = currentWebKey;
        catalogRequest = client
            .post("/esim/data")
            .then((response) => getEsimCatalog(response?.data))
            .catch((error) => {
                catalogRequest = null;
                throw error;
            });
    }

    return catalogRequest;
};

function useEsimApi() {
    const dispatch = useDispatch();
    const MerchantWebPortalAxios = useMerchantWebPortalAxios();
    const { errorResponse } = useResponse();
    const errorRes = {
        response: {
            data: {
                isPopup: true,
                PopupTitle: "Error",
                PopupDescription: "Something went wrong. Please try again later.",
            },
        },
    };

    const GetAllAvailableCountries = async () => {
        try {
            dispatch(settingBillsData({field: "isloading", value: "pending"}));
            const catalog = await loadCatalog(MerchantWebPortalAxios);
            return {
                status: true,
                data: buildCatalogOptions(catalog),
            };
        } catch (error) {
            errorResponse(errorRes);
            throw error;
        }
    }

    const GetLocalPlans = async () => {
        try {
            dispatch(settingBillsData({field: "isloading", value: "pending"}));
            const catalog = await loadCatalog(MerchantWebPortalAxios);
            return {
                status: true,
                data: buildCountryGroups(catalog),
            };
        } catch (error) {
            errorResponse(errorRes);
            throw error;
        }
    }

    const GetPlansByCountry = async (country) => {
        try {
            dispatch(settingBillsData({field: "isloading", value: "pending"}));
            const catalog = await loadCatalog(MerchantWebPortalAxios);
            return {
                status: true,
                data: {
                    plans: buildPlansForDestination(catalog, country),
                },
            };
        } catch (error) {
            errorResponse(errorRes);
            throw error;
        }
    }
    const getContientAvailableByCountry = async (country) => {
        try {
            dispatch(settingBillsData({field: "isloading", value: "idle"}));
            const catalog = await loadCatalog(MerchantWebPortalAxios);
            return {
                status: true,
                data: [
                    {
                        [country]: buildCoverageForDestination(catalog, country),
                    },
                ],
            };
        } catch (error) {
            errorResponse(errorRes);
            throw error;
        }
    }

    const GetEsimDetails = async (planId) => {
        try {
            dispatch(settingBillsData({field: "loading", value: true}));
            const res = await MerchantWebPortalAxios.post(`/esim/GetEsimDetails`, {esimId: planId});
            return {
                ...res?.data,
                data: res?.data?.message,
            };
        } catch (error) {
            // errorResponse(errorRes);
            throw error;
        }
    }

    const GetNetworksById = async (planId) => {
        try {
            const res = await MerchantWebPortalAxios.get(`/esim/getNetworksById?planId=${encodeURIComponent(planId)}`);
            return {
                ...res?.data,
                data: res?.data?.message,
            };
        } catch (error) {
            // errorResponse(errorRes);
            throw error;
        }
    }

    const GetUsageOfESIM = async () => {
        try {
            dispatch(settingBillsData({field: "isloading", value: "pending"}));
            const res = await MerchantWebPortalAxios.post(`/esim/getallesims`, {});
            return {
                ...res?.data,
                data: res?.data?.message,
            };
        } catch (error) {
            // errorResponse(errorRes);
            throw error;
        }
    };

    const PurchaseTopupEsim = async (payload) => {
        dispatch(settingBillsData({field: "isloading", value: "pending"}));
        const purchasePayload = buildCorporatePurchasePayload(payload);
        const res = await MerchantWebPortalAxios.post(`/esim/purchase`, purchasePayload);

        if (res?.data?.status !== true || res?.data?.success !== true) {
            const error = new Error(res?.data?.message || "Unable to complete eSIM purchase");
            error.response = {
                data: {
                    isPopup: true,
                    PopupDescription: res?.data?.SubTitle || res?.data?.message || error.message,
                    PopupTitle: res?.data?.Title || "Unable to complete eSIM purchase",
                },
                status: 400,
            };
            throw error;
        }

        return buildCorporatePurchaseResponse(res.data, payload);
    };


    return {
        getContientAvailableByCountry,
        GetAllAvailableCountries,
        GetPlansByCountry,
        GetEsimDetails,
        GetNetworksById,
        GetUsageOfESIM,
        PurchaseTopupEsim,
        GetLocalPlans
    };
}

export default useEsimApi;
